import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, Truck, FileText, ClipboardList, Inbox, Trash2, Download, Plus, Loader2, BadgeCheck, CheckCircle2, Star, CalendarCheck, Activity as ActivityIcon, CreditCard, MessageSquare, UserPlus, Bell, GraduationCap, Gift, Briefcase, Settings, Save } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { API } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const monthKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
};

const buildMonthlyRevenue = (payments) => {
  const map = {};
  payments.filter((p) => p.payment_status === "paid" && p.created_at).forEach((p) => {
    const k = monthKey(p.created_at);
    map[k] = (map[k] || 0) + Number(p.usd_amount || 0);
  });
  // Show a continuous last-12-month window ending this month.
  const now = new Date();
  const out = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ month: monthLabel(k), revenue: Math.round((map[k] || 0) * 100) / 100 });
  }
  return out;
};

const exportPaymentsCsv = (payments) => {
  const headers = ["Date", "Customer Name", "Email", "Item", "Package", "Amount", "Currency", "USD Amount", "Method", "Status"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = payments.map((p) => [
    p.created_at ? new Date(p.created_at).toLocaleString() : "",
    p.name || "", p.email || "", p.label || "", p.package_id || "",
    p.amount ?? "", p.currency || "", p.usd_amount ?? "", p.provider || "", p.payment_status || "",
  ].map(esc).join(","));
  const csv = [headers.map(esc).join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ffd-payments-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const TABS = [
  { key: "activity", label: "Activity", icon: Bell },
  { key: "loads", label: "Loads", icon: Truck },
  { key: "files", label: "Documents", icon: FileText },
  { key: "fmcsa", label: "FMCSA Apps", icon: ClipboardList },
  { key: "contacts", label: "Leads", icon: Inbox },
  { key: "brokers", label: "Broker Apps", icon: BadgeCheck },
  { key: "brokerloads", label: "Broker Loads", icon: Truck },
  { key: "consults", label: "Consultations", icon: CalendarCheck },
  { key: "webintake", label: "Website Intakes", icon: FileText },
  { key: "testimonials", label: "Testimonials", icon: Star },
  { key: "enrollments", label: "Enrollments", icon: GraduationCap },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "referrals", label: "Referrals", icon: Gift },
  { key: "jobs", label: "Jobs", icon: Briefcase },
  { key: "applications", label: "Applications", icon: ClipboardList },
  { key: "subscribers", label: "Subscribers", icon: UserPlus },
  { key: "content", label: "Site Content", icon: Settings },
];

const ACTIVITY_ICON = {
  consultation: CalendarCheck,
  payment: CreditCard,
  lead: Inbox,
  fmcsa: ClipboardList,
  broker: BadgeCheck,
  brokerload: Truck,
  subscriber: UserPlus,
  review: MessageSquare,
  testimonial: Star,
};

const emptyLoad = { origin: "", destination: "", equipment: "Dry Van", miles: 0, pickup: "ASAP", rate: "", commodity: "", weight: "", note: "" };

const Admin = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("activity");
  const [activity, setActivity] = useState([]);
  const [loads, setLoads] = useState([]);
  const [files, setFiles] = useState([]);
  const [apps, setApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [brokerApps, setBrokerApps] = useState([]);
  const [brokerLoads, setBrokerLoads] = useState([]);
  const [consults, setConsults] = useState([]);
  const [webintakes, setWebintakes] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paySummary, setPaySummary] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [content, setContent] = useState(null);
  const [newJob, setNewJob] = useState({ title: "", equipment: "", pay: "", location: "", description: "" });
  const [savingContent, setSavingContent] = useState(false);
  const [newLoad, setNewLoad] = useState(emptyLoad);
  const [busy, setBusy] = useState(false);

  const token = localStorage.getItem("ffd_token");

  const loadAll = useCallback(async () => {
    const [l, f, a, c, ba, bl, cs, wi, tr, sb, ac, en, rf, jb, ja, ct, pm] = await Promise.allSettled([
      api.get("/loads"),
      api.get("/files/all"),
      api.get("/fmcsa-applications"),
      api.get("/contact"),
      api.get("/brokers/applications"),
      api.get("/brokers/loads"),
      api.get("/consultations"),
      api.get("/website-intake"),
      api.get("/service-reviews"),
      api.get("/subscribers"),
      api.get("/activity"),
      api.get("/enrollments"),
      api.get("/referrals"),
      api.get("/jobs"),
      api.get("/job-applications"),
      api.get("/content"),
      api.get("/payments"),
    ]);
    if (l.status === "fulfilled") setLoads(l.value.data);
    if (f.status === "fulfilled") setFiles(f.value.data);
    if (a.status === "fulfilled") setApps(a.value.data);
    if (c.status === "fulfilled") setContacts(c.value.data);
    if (ba.status === "fulfilled") setBrokerApps(ba.value.data);
    if (bl.status === "fulfilled") setBrokerLoads(bl.value.data);
    if (cs.status === "fulfilled") setConsults(cs.value.data);
    if (wi.status === "fulfilled") setWebintakes(wi.value.data);
    if (tr.status === "fulfilled") setTestimonials(tr.value.data.reviews || []);
    if (sb.status === "fulfilled") setSubscribers(sb.value.data);
    if (ac.status === "fulfilled") setActivity(ac.value.data);
    if (en.status === "fulfilled") setEnrollments(en.value.data);
    if (rf.status === "fulfilled") setReferrals(rf.value.data);
    if (jb.status === "fulfilled") setJobs(jb.value.data);
    if (ja.status === "fulfilled") setApplications(ja.value.data);
    if (ct.status === "fulfilled") setContent(ct.value.data);
    if (pm.status === "fulfilled") { setPayments(pm.value.data.payments || []); setPaySummary(pm.value.data.summary || null); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const addLoad = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { commodity, weight, note, ...base } = newLoad;
      const details = {};
      if (commodity) details.commodity = commodity;
      if (weight) details.weight = weight;
      if (note) details.note = note;
      await api.post("/loads", {
        ...base,
        miles: Number(newLoad.miles) || 0,
        details: Object.keys(details).length ? details : null,
      });
      setNewLoad(emptyLoad);
      loadAll();
    } finally { setBusy(false); }
  };

  const deleteLoad = async (id) => { await api.delete(`/loads/${id}`); loadAll(); };

  const addJob = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/jobs", newJob); setNewJob({ title: "", equipment: "", pay: "", location: "", description: "" }); loadAll(); }
    finally { setBusy(false); }
  };
  const deleteJob = async (id) => { await api.delete(`/jobs/${id}`); loadAll(); };

  const refundPayment = async (p) => {
    if (!window.confirm(`Refund ${p.currency} ${Number(p.amount).toLocaleString()} to ${p.email || "this customer"}? This returns their money via ${p.provider} and cannot be undone.`)) return;
    setBusy(true);
    try {
      const path = p.provider === "paypal" ? `/paypal/refund/${p.session_id}` : `/payments/refund/${p.session_id}`;
      await api.post(path);
      alert("Refund issued successfully.");
      loadAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Refund failed. Please try again or refund from your Stripe/PayPal dashboard.");
    } finally { setBusy(false); }
  };

  const setIntake = (k, v) => setContent((c) => ({ ...c, intake: { ...(c?.intake || {}), [k]: v } }));
  const setTraining = (k, v) => setContent((c) => ({ ...c, training: { ...(c?.training || {}), [k]: v } }));
  const saveContent = async () => {
    setSavingContent(true);
    try { const r = await api.put("/content", { data: content }); setContent(r.data); alert("Site content saved."); }
    catch (e) { alert("Could not save. Are you logged in as admin?"); }
    finally { setSavingContent(false); }
  };

  return (
    <div className="dash">
      <div className="dash-top">
        <div className="container dash-top-row">
          <Link to="/" className="dash-brand">FLYING FLEET <span>DISPATCH</span></Link>
          <div className="dash-user">
            <Link to="/portal" className="dash-link">My Portal</Link>
            <span className="dash-name">{user?.name} · Admin</span>
            <button className="dash-logout" onClick={logout}><LogOut size={16} /> Log out</button>
          </div>
        </div>
      </div>

      <div className="container dash-body">
        <div className="dash-head"><h1>Admin Dashboard</h1><p>Manage loads and review incoming documents, filings, and leads.</p></div>

        <div className="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count = { activity: activity.length, loads: loads.length, files: files.length, fmcsa: apps.length, contacts: contacts.length, brokers: brokerApps.length, brokerloads: brokerLoads.length, consults: consults.length, webintake: webintakes.length, testimonials: testimonials.length, subscribers: subscribers.length, enrollments: enrollments.length, payments: payments.length, referrals: referrals.length, jobs: jobs.length, applications: applications.length }[t.key];
            return (
              <button key={t.key} className={`admin-tab ${tab === t.key ? "on" : ""}`} onClick={() => setTab(t.key)}>
                <Icon size={16} /> {t.label} <span className="tab-count">{count}</span>
              </button>
            );
          })}
        </div>

        {tab === "activity" && (
          <div className="activity-wrap">
            {activity.length === 0 && <div className="empty-td" style={{ padding: "40px", textAlign: "center", color: "var(--grey-500)" }}>No activity yet. New leads, payments, and sign-ups will appear here.</div>}
            <ul className="activity-feed">
              {activity.map((it, i) => {
                const Icon = ACTIVITY_ICON[it.kind] || ActivityIcon;
                return (
                  <li key={i} className={`activity-item k-${it.kind}`}>
                    <span className="activity-ico"><Icon size={17} /></span>
                    <div className="activity-body">
                      <span className="activity-title">{it.title}</span>
                      <span className="activity-time">{new Date(it.created_at).toLocaleString()}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {tab === "payments" && (
          <div className="admin-panel" data-testid="payments-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "18px" }}>
              <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px", color: "#0a2447" }}><CreditCard size={18} /> Monthly Revenue (paid, USD)</h3>
              <button className="btn btn-primary" style={{ padding: "10px 16px", fontSize: "13px" }} onClick={() => exportPaymentsCsv(payments)} disabled={payments.length === 0} data-testid="export-payments-csv"><Download size={15} /> Download CSV</button>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e6ebf1", borderRadius: "14px", padding: "18px 14px 8px", marginBottom: "22px", height: "260px" }} data-testid="revenue-chart">
              {(paySummary?.paid_count ?? 0) === 0 ? (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--grey-500, #8a94a6)", textAlign: "center", fontSize: "14px" }}>
                  No completed payments yet. Your monthly revenue graph will fill in as real payments come through.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={buildMonthlyRevenue(payments)} margin={{ top: 6, right: 10, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f5" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7688" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: "#6b7688" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, "Revenue"]} cursor={{ fill: "rgba(99,194,106,0.08)" }} />
                    <Bar dataKey="revenue" fill="#63c26a" radius={[6, 6, 0, 0]} maxBarSize={54} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="pay-summary" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px", marginBottom: "22px" }}>
              <div className="pay-stat" data-testid="pay-total-collected" style={{ background: "var(--navy, #0a2447)", color: "#fff", borderRadius: "14px", padding: "18px 20px" }}>
                <div style={{ fontSize: "12px", opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Collected</div>
                <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px" }}>${(paySummary?.total_collected_usd ?? 0).toLocaleString()}</div>
              </div>
              <div className="pay-stat" data-testid="pay-month-collected" style={{ background: "#e8f6ea", color: "#0a2447", borderRadius: "14px", padding: "18px 20px", border: "1px solid #cdeccf" }}>
                <div style={{ fontSize: "12px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.06em" }}>This Month</div>
                <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px" }}>${(paySummary?.month_collected_usd ?? 0).toLocaleString()}</div>
              </div>
              <div className="pay-stat" style={{ background: "#f5f7fa", color: "#0a2447", borderRadius: "14px", padding: "18px 20px", border: "1px solid #e6ebf1" }}>
                <div style={{ fontSize: "12px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.06em" }}>Paid</div>
                <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px" }}>{paySummary?.paid_count ?? 0}</div>
              </div>
              <div className="pay-stat" style={{ background: "#f5f7fa", color: "#0a2447", borderRadius: "14px", padding: "18px 20px", border: "1px solid #e6ebf1" }}>
                <div style={{ fontSize: "12px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.06em" }}>Pending</div>
                <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px" }}>{paySummary?.pending_count ?? 0}</div>
              </div>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Customer</th><th>Item</th><th>Amount</th><th>Method</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
                <tbody>
                  {payments.length === 0 && <tr><td colSpan={7} className="empty-td">No payments yet. Stripe & PayPal transactions will appear here.</td></tr>}
                  {payments.map((p) => (
                    <tr key={p.id} data-testid="payment-row">
                      <td>{p.name || "—"}<div className="lead-contact">{p.email || "—"}</div></td>
                      <td>{p.label || p.package_id}</td>
                      <td className="rate">{p.currency} {Number(p.amount || 0).toLocaleString()}{p.currency !== "USD" && <div className="lead-contact">≈ ${Number(p.usd_amount || 0).toLocaleString()}</div>}</td>
                      <td><span className="tag" style={{ textTransform: "capitalize" }}>{p.provider}</span></td>
                      <td>{p.refund_status === "refunded" ? <span style={{ background: "#fdecec", color: "#b3261e", padding: "4px 10px", borderRadius: "20px", fontWeight: 700, fontSize: "12px" }}>Refunded</span> : <span style={{ background: p.payment_status === "paid" ? "#e8f6ea" : "#fff3e0", color: p.payment_status === "paid" ? "#1a7a2e" : "#a15c00", padding: "4px 10px", borderRadius: "20px", fontWeight: 700, fontSize: "12px", textTransform: "capitalize" }}>{p.payment_status}</span>}</td>
                      <td>{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                      <td>{p.payment_status === "paid" && p.refund_status !== "refunded" ? <button className="file-btn danger" style={{ padding: "6px 12px", fontSize: "12px", fontWeight: 700 }} disabled={busy} onClick={() => refundPayment(p)} data-testid="refund-btn">Refund</button> : <span style={{ color: "var(--grey-500, #8a94a6)", fontSize: "12px" }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "loads" && (
          <div className="admin-panel">
            <form className="load-form" onSubmit={addLoad}>
              <h3><Plus size={16} /> Add a load</h3>
              <div className="load-form-grid">
                <input required placeholder="Origin (e.g. Chicago, IL)" value={newLoad.origin} onChange={(e) => setNewLoad({ ...newLoad, origin: e.target.value })} />
                <input required placeholder="Destination" value={newLoad.destination} onChange={(e) => setNewLoad({ ...newLoad, destination: e.target.value })} />
                <input placeholder="Equipment" value={newLoad.equipment} onChange={(e) => setNewLoad({ ...newLoad, equipment: e.target.value })} />
                <input type="number" placeholder="Miles" value={newLoad.miles} onChange={(e) => setNewLoad({ ...newLoad, miles: e.target.value })} />
                <input placeholder="Pickup (e.g. ASAP)" value={newLoad.pickup} onChange={(e) => setNewLoad({ ...newLoad, pickup: e.target.value })} />
                <input required placeholder="Rate (e.g. $2,450)" value={newLoad.rate} onChange={(e) => setNewLoad({ ...newLoad, rate: e.target.value })} />
                <input placeholder="Commodity (optional)" value={newLoad.commodity} onChange={(e) => setNewLoad({ ...newLoad, commodity: e.target.value })} />
                <input placeholder="Weight (optional, e.g. 42,000 lbs)" value={newLoad.weight} onChange={(e) => setNewLoad({ ...newLoad, weight: e.target.value })} />
                <input placeholder="Note (optional)" value={newLoad.note} onChange={(e) => setNewLoad({ ...newLoad, note: e.target.value })} />
              </div>
              <button className="btn btn-primary" disabled={busy}>{busy ? <Loader2 size={16} className="spin" /> : <Plus size={16} />} Add Load</button>
            </form>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Route</th><th>Equipment</th><th>Miles</th><th>Pickup</th><th>Rate</th><th></th></tr></thead>
                <tbody>
                  {loads.length === 0 && <tr><td colSpan={6} className="empty-td">No loads yet.</td></tr>}
                  {loads.map((l) => (
                    <tr key={l.id}>
                      <td>{l.origin} → {l.destination}</td>
                      <td>{l.equipment}</td>
                      <td>{l.miles} mi</td>
                      <td>{l.pickup}</td>
                      <td className="rate">{l.rate}</td>
                      <td><button className="file-btn danger" onClick={() => deleteLoad(l.id)}><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "files" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Carrier</th><th>Type</th><th>File</th><th>Size</th><th>Uploaded</th><th></th></tr></thead>
              <tbody>
                {files.length === 0 && <tr><td colSpan={6} className="empty-td">No documents uploaded yet.</td></tr>}
                {files.map((f) => (
                  <tr key={f.id}>
                    <td>{f.user_name}</td>
                    <td><span className="tag">{f.doc_type}</span></td>
                    <td>{f.original_name}</td>
                    <td>{(f.size / 1024).toFixed(0)} KB</td>
                    <td>{new Date(f.uploaded_at).toLocaleDateString()}</td>
                    <td><a className="file-btn" href={`${API}/files/download/${f.id}?auth=${token}`} target="_blank" rel="noreferrer"><Download size={15} /></a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "fmcsa" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Business</th><th>Contact</th><th>Filing</th><th>Type</th><th>State</th><th>Date</th></tr></thead>
              <tbody>
                {apps.length === 0 && <tr><td colSpan={7} className="empty-td">No FMCSA applications yet.</td></tr>}
                {apps.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}</td>
                    <td>{a.business || "—"}</td>
                    <td><div className="lead-contact">{a.email}<br />{a.phone}</div></td>
                    <td>{a.filingType}</td>
                    <td>{a.businessType}</td>
                    <td>{a.state || "—"}</td>
                    <td>{new Date(a.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "contacts" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Contact</th><th>Equipment</th><th>Kind</th><th>Message</th><th>Date</th></tr></thead>
              <tbody>
                {contacts.length === 0 && <tr><td colSpan={6} className="empty-td">No leads yet.</td></tr>}
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td><div className="lead-contact">{c.email}<br />{c.phone}</div></td>
                    <td>{c.equipment || "—"}</td>
                    <td><span className="tag">{c.kind}</span></td>
                    <td className="msg-cell">{c.message || "—"}</td>
                    <td>{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "brokers" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Company</th><th>Contact</th><th>MC #</th><th>Status</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {brokerApps.length === 0 && <tr><td colSpan={6} className="empty-td">No broker applications yet.</td></tr>}
                {brokerApps.map((b) => (
                  <tr key={b.id}>
                    <td>{b.company}<div className="lead-contact">{b.contact_name}</div></td>
                    <td><div className="lead-contact">{b.email}<br />{b.phone}</div></td>
                    <td>{b.mc_number}</td>
                    <td>{b.verified ? <span className="tag" style={{ background: "rgba(63,174,74,0.12)", color: "#2e8b39" }}>Verified</span> : <span className="tag">Pending</span>}</td>
                    <td>{new Date(b.created_at).toLocaleDateString()}</td>
                    <td>{!b.verified && <button className="btn btn-primary" style={{ padding: "8px 14px", fontSize: "12px" }} onClick={async () => { await api.patch(`/brokers/applications/${b.id}/verify`); loadAll(); }}><CheckCircle2 size={14} /> Verify</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "brokerloads" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Broker</th><th>Route</th><th>Equipment</th><th>Rate</th><th>Notes</th><th>Date</th></tr></thead>
              <tbody>
                {brokerLoads.length === 0 && <tr><td colSpan={6} className="empty-td">No broker-submitted loads yet.</td></tr>}
                {brokerLoads.map((b) => (
                  <tr key={b.id}>
                    <td>{b.company}<div className="lead-contact">{b.email}</div></td>
                    <td>{b.origin} → {b.destination}</td>
                    <td>{b.equipment}</td>
                    <td className="rate">{b.rate}</td>
                    <td className="msg-cell">{b.notes || "—"}</td>
                    <td>{new Date(b.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "consults" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Contact</th><th>Service</th><th>Budget</th><th>Preferred</th><th>Details</th><th>Date</th></tr></thead>
              <tbody>
                {consults.length === 0 && <tr><td colSpan={7} className="empty-td">No consultation requests yet.</td></tr>}
                {consults.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}<div className="lead-contact">{c.business || ""}</div></td>
                    <td><div className="lead-contact">{c.email}<br />{c.phone}</div></td>
                    <td><span className="tag">{c.service_type}</span></td>
                    <td>{c.budget || "—"}</td>
                    <td>{c.preferred_date || "—"}</td>
                    <td className="msg-cell">{c.details || "—"}</td>
                    <td>{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "webintake" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Company</th><th>Contact</th><th>Plan</th><th>Pages</th><th>Features</th><th>Date</th></tr></thead>
              <tbody>
                {webintakes.length === 0 && <tr><td colSpan={6} className="empty-td">No website intakes yet.</td></tr>}
                {webintakes.map((w) => (
                  <tr key={w.id}>
                    <td>{w.company_name}<div className="lead-contact">{(w.business_types || []).join(", ")}</div></td>
                    <td><div className="lead-contact">{w.contact_name}<br />{w.contact_info}</div></td>
                    <td>{w.plan || "—"}</td>
                    <td className="msg-cell">{(w.pages || []).join(", ") || "—"}</td>
                    <td className="msg-cell">{(w.additional_features || []).join(", ") || "—"}</td>
                    <td>{new Date(w.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "testimonials" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Company</th><th>Rating</th><th>Review</th><th>Date</th></tr></thead>
              <tbody>
                {testimonials.length === 0 && <tr><td colSpan={5} className="empty-td">No testimonials yet.</td></tr>}
                {testimonials.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>{t.company || "—"}</td>
                    <td className="rate">{t.rating}★</td>
                    <td className="msg-cell">{t.comment || "—"}</td>
                    <td>{new Date(t.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "subscribers" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Email</th><th>Name</th><th>Subscribed</th></tr></thead>
              <tbody>
                {subscribers.length === 0 && <tr><td colSpan={3} className="empty-td">No subscribers yet.</td></tr>}
                {subscribers.map((s) => (
                  <tr key={s.id}>
                    <td>{s.email}</td>
                    <td>{s.name || "—"}</td>
                    <td>{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "enrollments" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Plan</th><th>Referral</th><th>Date</th></tr></thead>
              <tbody>
                {enrollments.length === 0 && <tr><td colSpan={6} className="empty-td">No enrollments yet.</td></tr>}
                {enrollments.map((en) => (
                  <tr key={en.id || en._id || en.email + en.created_at}>
                    <td>{en.name}</td>
                    <td className="lead-contact">{en.email}</td>
                    <td>{en.phone || "—"}</td>
                    <td><span className="tag">{en.plan}</span></td>
                    <td>{en.referral_code || "—"}</td>
                    <td>{en.created_at ? new Date(en.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "referrals" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Code</th><th>Name</th><th>Email</th><th>Referrals</th><th>Date</th></tr></thead>
              <tbody>
                {referrals.length === 0 && <tr><td colSpan={5} className="empty-td">No referral signups yet.</td></tr>}
                {referrals.map((rf) => (
                  <tr key={rf.code}>
                    <td className="rate">{rf.code}</td>
                    <td>{rf.name}</td>
                    <td className="lead-contact">{rf.email}</td>
                    <td>{rf.referrals ?? 0}</td>
                    <td>{rf.created_at ? new Date(rf.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "jobs" && (
          <div className="admin-panel">
            <form className="load-form" onSubmit={addJob}>
              <h3><Plus size={16} /> Post a driver job</h3>
              <div className="load-form-grid">
                <input required placeholder="Title (e.g. Box Truck Driver)" value={newJob.title} onChange={(e) => setNewJob({ ...newJob, title: e.target.value })} />
                <input placeholder="Equipment (e.g. 26ft box)" value={newJob.equipment} onChange={(e) => setNewJob({ ...newJob, equipment: e.target.value })} />
                <input placeholder="Pay (e.g. 70% linehaul)" value={newJob.pay} onChange={(e) => setNewJob({ ...newJob, pay: e.target.value })} />
                <input placeholder="Location (e.g. Nationwide)" value={newJob.location} onChange={(e) => setNewJob({ ...newJob, location: e.target.value })} />
                <input placeholder="Short description" value={newJob.description} onChange={(e) => setNewJob({ ...newJob, description: e.target.value })} />
              </div>
              <button className="btn btn-primary" disabled={busy}>{busy ? <Loader2 size={16} className="spin" /> : <Plus size={16} />} Post Job</button>
            </form>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Title</th><th>Equipment</th><th>Pay</th><th>Location</th><th></th></tr></thead>
                <tbody>
                  {jobs.length === 0 && <tr><td colSpan={5} className="empty-td">No active jobs.</td></tr>}
                  {jobs.map((j) => (
                    <tr key={j.id}>
                      <td>{j.title}</td><td>{j.equipment}</td><td>{j.pay || "—"}</td><td>{j.location || "—"}</td>
                      <td><button className="file-btn danger" onClick={() => deleteJob(j.id)}><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "applications" && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Contact</th><th>Driver Type</th><th>Equipment</th><th>Authority</th><th>Applied For</th><th>Date</th></tr></thead>
              <tbody>
                {applications.length === 0 && <tr><td colSpan={7} className="empty-td">No driver applications yet.</td></tr>}
                {applications.map((ap) => (
                  <tr key={ap.id}>
                    <td>{ap.name}</td>
                    <td><div className="lead-contact">{ap.email}<br />{ap.phone}</div></td>
                    <td><span className="tag">{ap.driver_type}</span></td>
                    <td>{ap.equipment || "—"}</td>
                    <td>{ap.authority || "—"}</td>
                    <td className="msg-cell">{ap.job_title || "—"}</td>
                    <td>{ap.created_at ? new Date(ap.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "content" && content && (
          <div className="admin-panel content-editor">
            <div className="content-card">
              <h3><Settings size={16} /> Training page text</h3>
              <label>Hero title</label>
              <input value={content.training?.hero_title || ""} onChange={(e) => setTraining("hero_title", e.target.value)} />
              <label>Hero subtitle</label>
              <textarea value={content.training?.hero_subtitle || ""} onChange={(e) => setTraining("hero_subtitle", e.target.value)} />
              <label>Deposit note</label>
              <input value={content.training?.deposit_note || ""} onChange={(e) => setTraining("deposit_note", e.target.value)} />
            </div>

            <div className="content-card">
              <h3><FileText size={16} /> Website Intake form fields</h3>
              <label>Form title</label>
              <input value={content.intake?.title || ""} onChange={(e) => setIntake("title", e.target.value)} />
              <label>Form description</label>
              <textarea value={content.intake?.description || ""} onChange={(e) => setIntake("description", e.target.value)} />
              <label>Business types (comma-separated)</label>
              <input value={(content.intake?.business_types || []).join(", ")} onChange={(e) => setIntake("business_types", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
              <label>Equipment options (comma-separated)</label>
              <input value={(content.intake?.equipment || []).join(", ")} onChange={(e) => setIntake("equipment", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
              <label>Pages options (comma-separated)</label>
              <input value={(content.intake?.pages || []).join(", ")} onChange={(e) => setIntake("pages", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
              <label>Feature options (comma-separated)</label>
              <input value={(content.intake?.features || []).join(", ")} onChange={(e) => setIntake("features", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
            </div>

            <div className="content-card">
              <h3><Briefcase size={16} /> Embed on Squarespace</h3>
              <p className="content-hint">Paste this into a Squarespace <strong>Code block</strong> to embed your live training page inside your Squarespace site:</p>
              <textarea readOnly className="embed-code" value={`<iframe src="${window.location.origin}/training" width="100%" height="1200" style="border:0;" title="Flying Fleet Dispatch Training"></iframe>`} />
              <p className="content-hint">Or simply link a Squarespace button to: <strong>{window.location.origin}/training</strong></p>
            </div>

            <button className="btn btn-primary" onClick={saveContent} disabled={savingContent} data-testid="save-content-btn">{savingContent ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save Site Content</button>
          </div>
        )}

      </div>
    </div>
  );
};

export default Admin;
